
import React from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { CalendarIcon, ArrowRight, Clock, MapPin } from 'lucide-react';

interface BlogPost {
  id: string;
  title: string;
  content: string;
  excerpt: string | null;
  featured_image: string | null;
  slug: string;
  author: string;
  published_date: string | null;
  status: 'draft' | 'published';
  seo_title: string | null;
  seo_description: string | null;
  seo_keywords: string[] | null;
  categories: string[] | null;
  tags: string[] | null;
  created_at: string | null;
  updated_at: string | null;
}

interface BlogPostCardProps {
  post: BlogPost;
  postCity: string | null;
}

const BlogPostCard: React.FC<BlogPostCardProps> = ({ post, postCity }) => {
  return (
    <Card className="overflow-hidden hover:shadow-xl transition-all duration-300 border-0 shadow-lg bg-white">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-0">
        {post.featured_image && (
          <div className="h-64 md:h-auto relative overflow-hidden">
            <img 
              src={post.featured_image} 
              alt={post.title}
              className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
          </div>
        )}
        <div className={`${post.featured_image ? 'col-span-2' : 'col-span-3'} p-8`}>
          <div className="flex items-center space-x-3 mb-4">
            {postCity && (
              <Badge variant="secondary" className="flex items-center gap-1 bg-brand-trophy-gold/10 text-brand-trophy-gold border-brand-trophy-gold/30">
                <MapPin className="h-3 w-3" />
                {postCity}
              </Badge>
            )}
            {post.categories && post.categories[0] && (
              <Badge variant="outline" className="border-brand-charcoal-gray/30 text-brand-charcoal-gray">{post.categories[0]}</Badge>
            )}
            <div className="flex items-center gap-1 text-sm text-gray-500">
              <Clock className="h-3 w-3" />
              2 min read
            </div>
          </div>
          <Link to={`/blog/${post.slug}`}>
            <h2 className="text-2xl font-bold mb-4 text-brand-charcoal-gray hover:text-brand-trophy-gold transition-colors leading-tight">
              {post.title}
            </h2>
          </Link>
          {post.excerpt && (
            <p className="text-gray-600 mb-6 leading-relaxed">{post.excerpt}</p>
          )}
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-500 flex items-center gap-2">
              <CalendarIcon className="h-4 w-4" />
              {new Date(post.published_date || '').toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </div>
            <Link 
              to={`/blog/${post.slug}`} 
              className="text-brand-trophy-gold font-semibold flex items-center hover:text-brand-safety-orange transition-colors group"
            >
              Read More 
              <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default BlogPostCard;
